import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { blogPosts } from "@/data/blogPosts";
import ReactMarkdown from "react-markdown";
import { typography } from "@/styles/typography";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface BusinessReportFormProps {
  businessType: string;
  normalizedType: string;
}

export default function BusinessReportForm({ businessType, normalizedType }: BusinessReportFormProps) {
  const [formData, setFormData] = useState({
    clientName: "",
    clientEmail: "",
    clientCompany: "",
    mainProblem: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);

  // Check if form is valid
  const isFormValid = 
    formData.clientName.trim() !== "" && 
    formData.clientEmail.trim() !== "" && 
    formData.clientCompany.trim() !== "" && 
    formData.mainProblem.trim() !== "";

  // Get ALL articles that have this business type in targetAudience, excluding non-actionable ones
  const relevantArticles = blogPosts.filter(post => 
    post.targetAudience?.includes(normalizedType) && 
    !post.excludeFromReports
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedData = {
      clientName: formData.clientName.trim(),
      clientEmail: formData.clientEmail.trim(),
      clientCompany: formData.clientCompany.trim(),
      mainProblem: formData.mainProblem.trim(),
    };
    
    if (!trimmedData.clientName || !trimmedData.clientEmail || !trimmedData.clientCompany || !trimmedData.mainProblem) {
      toast.error("Por favor, completa todos los campos");
      return;
    }
    
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedData.clientEmail)) {
      toast.error("Por favor, introduce un email válido");
      return;
    }

    setIsLoading(true);
    setReport(null);

    try {
      // Prepare all articles for semantic search
      const allArticlesForSearch = blogPosts
        .filter(post => !post.excludeFromReports)
        .map(article => ({
          id: article.id,
          title: article.title,
          description: article.description,
          insight: article.insight,
          category: article.category,
          content: article.content?.substring(0, 400),
          businessSolution: article.businessSolution,
          realWorldExample: article.realWorldExample,
        }));

      // Step 1: Semantic search to find articles relevant to the user's problem
      let rankedArticleIds: string[] = [];
      try {
        const searchResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/semantic-search`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({
              query: `${normalizedType}: ${trimmedData.mainProblem}`,
              articles: allArticlesForSearch,
            }),
          }
        );
        const searchData = await searchResponse.json();
        if (searchData.results?.length > 0) {
          rankedArticleIds = searchData.results
            .sort((a: any, b: any) => b.relevance - a.relevance)
            .map((r: any) => r.id);
        }
      } catch (searchError) {
        console.error("Semantic search failed, using default articles:", searchError);
      }

      // Step 2: Build final article list - semantic results first, then sector defaults
      const sectorArticles = relevantArticles.map(a => ({
        id: a.id, title: a.title, description: a.description,
        insight: a.insight, businessSolution: a.businessSolution, realWorldExample: a.realWorldExample,
      }));

      let articles;
      if (rankedArticleIds.length > 0) {
        // Merge: semantic top results + sector articles, deduplicated
        const semanticArticles = rankedArticleIds
          .map(id => allArticlesForSearch.find(a => a.id === id))
          .filter(Boolean)
          .map(a => ({
            id: a!.id, title: a!.title, description: a!.description,
            insight: a!.insight, businessSolution: a!.businessSolution, realWorldExample: a!.realWorldExample,
          }));
        const seen = new Set(semanticArticles.map(a => a.id));
        const extras = sectorArticles.filter(a => !seen.has(a.id));
        articles = [...semanticArticles.slice(0, 8), ...extras.slice(0, 4)];
      } else {
        articles = sectorArticles;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-business-report`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            businessType: normalizedType,
            clientName: trimmedData.clientName,
            clientEmail: trimmedData.clientEmail,
            clientCompany: trimmedData.clientCompany,
            mainProblem: trimmedData.mainProblem,
            articles,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Error al generar el informe");
      }

      const data = await response.json();
      setReport(data.report);
      setShowReportModal(true);
      toast.success("¡Informe generado correctamente!");
    } catch (error) {
      console.error("Error:", error);
      toast.error(error instanceof Error ? error.message : "Error al generar el informe");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className={`${typography.h2Medium} text-foreground`}>
              Tu Informe de Transformación Digital
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] px-6">
            <div className="prose prose-lg max-w-none dark:prose-invert py-4">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1 className={`${typography.h2Large} text-foreground mb-6`}>{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className={`${typography.h2Medium} text-foreground mt-8 mb-3`}>{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className={`${typography.paragraphLarge} text-foreground font-semibold mt-5 mb-2`}>{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className={`${typography.paragraphBase} text-muted-foreground mb-3`}>{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside space-y-2 mb-4 text-muted-foreground">{children}</ul>
                  ),
                  li: ({ children }) => (
                    <li className={`${typography.paragraphBase}`}>{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="text-foreground font-semibold">{children}</strong>
                  ),
                }}
              >
                {report || ""}
              </ReactMarkdown>
            </div>
          </ScrollArea>
          <div className="flex flex-col sm:flex-row gap-3 justify-end p-6 pt-4 border-t border-border">
      <Button
              onClick={() => {
                setShowReportModal(false);
                setReport(null);
                setFormData({ clientName: "", clientEmail: "", clientCompany: "", mainProblem: "" });
              }}
              variant="outline"
            >
              Cerrar
            </Button>
            <Button
              onClick={() => window.location.href = "/contacto"}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Agendar llamada gratuita →
            </Button>
          </div>
        </DialogContent>
      </Dialog>


    <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="clientName" className="text-foreground text-sm">Tu nombre</Label>
          <Input
            id="clientName"
            placeholder="María García"
            value={formData.clientName}
            onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
            className="bg-background border-border/50"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="clientEmail" className="text-foreground text-sm">Email</Label>
          <Input
            id="clientEmail"
            type="email"
            placeholder="maria@empresa.com"
            value={formData.clientEmail}
            onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
            className="bg-background border-border/50"
            disabled={isLoading}
          />
        </div>
      </div>
      
      <div className="space-y-1.5">
        <Label htmlFor="clientCompany" className="text-foreground text-sm">Nombre de tu empresa</Label>
        <Input
          id="clientCompany"
          placeholder="Mi Empresa S.L."
          value={formData.clientCompany}
          onChange={(e) => setFormData({ ...formData, clientCompany: e.target.value })}
          className="bg-background border-border/50"
          disabled={isLoading}
        />
      </div>
      
      <div className="space-y-1.5">
        <Label htmlFor="mainProblem" className="text-foreground text-sm">¿Cuál es tu mayor reto ahora mismo?</Label>
        <Textarea
          id="mainProblem"
          placeholder="Ej: Perdemos mucho tiempo en tareas repetitivas..."
          value={formData.mainProblem}
          onChange={(e) => setFormData({ ...formData, mainProblem: e.target.value })}
          className="bg-background border-border/50 min-h-[80px] resize-none"
          disabled={isLoading}
        />
      </div>

      <Button 
        type="submit" 
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-5 disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isLoading || !isFormValid}
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin">⚡</span>
            Analizando {relevantArticles.length} soluciones...
          </span>
        ) : (
          `Generar mi informe gratuito →`
        )}
      </Button>
      
      <p className="text-muted-foreground text-center text-sm">
        Sin compromiso · Personalizado en 30 segundos
      </p>
    </form>
    </>
  );
}
